<template>
  <v-container class="pa-6">
    <div v-if="loading" role="status" aria-live="polite" aria-busy="true" class="pa-6">
      <span class="d-sr-only">Loading tax estimator.</span>
      <v-skeleton-loader type="card" />
    </div>

    <v-card v-else class="pa-6" outlined>
      <v-card-title tag="h1" class="text-h5 font-weight-bold text-break">
        Vehicle Personal Property Tax Estimator
      </v-card-title>

      <section
        v-if="compiledMarkdownContent"
        class="px-4"
        aria-label="Estimator introduction"
        v-html="compiledMarkdownContent"
      ></section>

      <v-card-text>
        <v-form ref="form" aria-label="Tax estimate inputs">
          <v-row>
            <v-col cols="12">
              <v-card outlined><v-card-text>Vehicle Type: <strong>PERSONAL USE VEHICLE</strong></v-card-text></v-card>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="formattedInput"
                label="Estimated property value"
                @blur="onBlur"
                @focus="onFocus"
                prefix="$"
                outlined
                dense
                hide-details
                autocomplete="transaction-amount"
                inputmode="decimal"
              >
                <template v-slot:append-outer>
                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        type="button"
                        icon
                        small
                        aria-label="How estimated property value is determined"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small color="primary" aria-hidden="true">mdi-information-outline</v-icon>
                      </v-btn>
                    </template>
                    <span style="max-width: 200px; display: inline-block; white-space: normal;">
                      {{ pptraData.EstPropValueTooltip }}
                    </span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12">
              <v-card outlined><v-card-text>Tax year: <strong>{{ taxYear }}</strong></v-card-text></v-card>
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="monthsSelected"
                :items="[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]"
                label="Number of months (1 to 12)"
                outlined
                dense
                hide-details
              />
            </v-col>

            <v-col cols="12">
              <v-card outlined><v-card-text>Tax rate: <strong>{{ displayRate }}</strong></v-card-text></v-card>
            </v-col>

            <section
              v-if="compiledMarkdownInnerParagraph"
              class="px-4"
              aria-label="Estimator introduction"
              v-html="compiledMarkdownInnerParagraph"
            ></section>

            <v-col cols="12">
              <v-card outlined><v-card-text>PPTRA rate: <strong>{{ displayRegulateRate }}</strong></v-card-text></v-card>
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <section aria-labelledby="tax-before-heading">
            <v-row>
              <v-col cols="12">
                <h2 id="tax-before-heading" class="text-h6 font-weight-bold">Estimated tax liability before PPTRA</h2>
                <p class="text-h5 font-weight-bold text-primary">
                  ${{ fullTaxWithoutPPTRA.toFixed(2) | currency }}
                </p>
              </v-col>

              <v-col cols="12">
                <v-simple-table dense>
                  <template v-slot:default>
                    <caption class="text-left py-2 font-weight-medium">
                      PPTRA subsidy breakdown
                    </caption>
                    <thead>
                      <tr class="blue darken-2">
                        <th scope="col" class="text-left white--text text-h6 font-weight-bold">SUBSIDY:</th>
                        <th scope="col" class="text-left white--text text-h6 font-weight-bold d-sr-only">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <th scope="row" class="subsidy-row-label">PPTRA ON FIRST $4,000:</th>
                        <td class="text-right">${{ pptraFirstPortion.toFixed(2) | currency }}</td>
                      </tr>
                      <tr>
                        <th scope="row" class="subsidy-row-label">PPTRA ON NEXT $16,000:</th>
                        <td class="text-right">${{ pptraNextPortion.toFixed(2) | currency }}</td>
                      </tr>
                      <tr class="font-weight-bold">
                        <th scope="row">TOTAL PPTRA:</th>
                        <td class="text-right">${{ pptraTotal.toFixed(2) | currency }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>

              <v-col cols="12">
                <h2 :id="'tax-due-heading-' + taxYear" class="text-h6 font-weight-bold">
                  Estimated {{ taxYear }} tax liability
                </h2>
                <p class="text-h5 font-weight-bold text-primary">
                  ${{ taxDue.toFixed(2) | currency }}
                </p>
              </v-col>
            </v-row>
          </section>
        </v-form>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import service from '../../data/service';
import { marked } from 'marked';

marked.setOptions({
  breaks: true,
});

const PPTRA_FIRST = 4000
const PPTRA_NEXT = 16000

export default {
  data() {
    const currentYear = new Date().getFullYear();
    return {
      value: null,
      formattedInput: '',
      monthsSelected: 12,
      vehicleType: ['PRIVATE VEHICLE', 'GREEN VEHICLE', 'ACCESSIBLE VEHICLE'],
      vehicleTypeSelected: 'PRIVATE VEHICLE',
      taxYear: currentYear,
      taxYears: Array.from({ length: 4 }, (_, i) => currentYear - i),
      pptraData: {
        taxRate: null,
        ratesByYear: [],
        DescriptionParagraph: "",
        EstPropValueTooltip: "",
        InnerParagraph: ""
      },
      compiledMarkdownContent: '',
      compiledMarkdownInnerParagraph: '',
      loading: true 
    };
  },
  async mounted() {
    try {
      const data = await service.loadPPTRA();
      this.pptraData = data;
      this.compiledMarkdownContent = marked(data.DescriptionParagraph || '');
      this.compiledMarkdownInnerParagraph = marked(data.InnerParagraph || '');
    } catch (err) {
      console.error('Failed to load PPTRA data', err);
    } finally {
      this.loading = false;
    }
  },
  filters: {
    currency(value) {
      if (typeof value !== "number") {
        value = Number(value);
      }
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.formattedInput = this.formatCurrency(val);
      }
    }
  },
  methods: {
    formatCurrency(value) {
      if (typeof value !== 'number' || isNaN(value)) return '';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },
    onBlur() {
      const numeric = parseFloat(this.formattedInput.replace(/[^0-9.]/g, ''));
      this.value = isNaN(numeric) ? 0 : numeric;
      this.formattedInput = this.formatCurrency(this.value);
    },
    onFocus() {
      this.formattedInput = this.value?.toString() || '';
    }
  },
  computed: {
    formattedValue() {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(this.value);
    },
    totalTax() {
      return (this.value / 100) * (this.pptraData.taxRate / 100);
    },
    pptraFirstPortion() {
      return (Math.min(this.value, PPTRA_FIRST) * (this.pptraData.taxRate / 100) * (this.monthsSelected / 12));
    },
    pptraNextPortion() {
      if (this.value <= PPTRA_FIRST) return 0;

      const cappedValue = Math.min(this.value, PPTRA_FIRST + PPTRA_NEXT);
      const nextValue = cappedValue - PPTRA_FIRST;

      return nextValue * (this.monthsSelected / 12) * (this.pptraData.taxRate / 100) * this.regularRate;
    },
    pptraTotal() {
      return this.pptraFirstPortion + this.pptraNextPortion;
    },
    fullTaxWithoutPPTRA() {
      return this.value * (this.pptraData.taxRate / 100) * (this.monthsSelected / 12);
    },
    taxDue() {
      return this.fullTaxWithoutPPTRA - this.pptraTotal;
    },
    regularRate() {
      let foundYear = this.pptraData.ratesByYear.find(a => a['TAX YEAR'] == this.taxYear);

      if (!foundYear) {
        if (!this.pptraData.ratesByYear.length) return 0;

        foundYear = this.pptraData.ratesByYear.reduce((prev, current) => {
          return (prev['TAX YEAR'] > current['TAX YEAR']) ? prev : current;
        });
      }

      return foundYear?.[this.vehicleTypeSelected] ?? 0;
    },
    displayRegulateRate() {
      return `${(this.regularRate * 100).toFixed(2)}%`;
    },
    displayRate() {
      return `${this.pptraData.taxRate}%`;
    }
  }
};
</script>

<style scoped>
.text-primary {
  color: #1976d2;
}

.subsidy-row-label {
  font-weight: 400;
}
</style>
